import React from 'react'
import { Button, Dropdown, Menu, Icon, Spin, Tooltip } from 'antd'
import { ImagePreview } from '../../../../../components'
import CommentInput from '../../CommentInput'
import ImageHistoryModal from './ImageHistoryModal'
import EntitySelectorList from '../../EntitySelectorList'
import ImageCropperModal from '../../../../../components/ImageCropperModal'
import DialogueEditor from './DialogueEditor'
import DuplicateShotModal from './DuplicateShotModal'
import CopyImageModal from './CopyImageModal'

const ShotCard = ({
  DragHandle,
  shot,
  shotIndex,
  sceneId,
  formData,
  setFormData,
  handleDeleteShot,
  handleRegenerateImage,
  handleRegenerateWithReference,
  handleRegenerateWithReferenceNanoBanana,
  handleEditSettings,
  updateShotDataRef,
  handleFileUpload,
  handleCopyImageToShot,
  isUploadingImage,
  copyImageTargets,
  videoId,
  isFetchingCharacters
}) => {
  const [historyModalVisible, setHistoryModalVisible] = React.useState(false)
  const [cropperModalVisible, setCropperModalVisible] = React.useState(false)
  const [duplicateModalVisible, setDuplicateModalVisible] = React.useState(
    false
  )
  const [copyImageModalVisible, setCopyImageModalVisible] = React.useState(
    false
  )
  const [selectedImageFile, setSelectedImageFile] = React.useState(null)
  const fileInputRef = React.useRef(null)

  const availableCopyTargets = React.useMemo(
    () =>
      (copyImageTargets || []).filter(
        target => !(target.sceneId === sceneId && target.shotId === shot.id)
      ),
    [copyImageTargets, sceneId, shot.id]
  )

  const sourceLabel = React.useMemo(() => {
    const currentTarget = (copyImageTargets || []).find(
      target => target.sceneId === sceneId && target.shotId === shot.id
    )

    return currentTarget?.label || `Shot ${shotIndex}`
  }, [copyImageTargets, sceneId, shot.id, shotIndex])

  const handleFileChange = e => {
    const file = e.target.files[0]
    if (file) {
      // Open cropper modal instead of direct upload
      setSelectedImageFile(file)
      setCropperModalVisible(true)
    }
    // Reset input so the same file can be uploaded again if needed
    e.target.value = ''
  }

  const handleCropComplete = croppedFile => {
    // Upload the cropped file
    handleFileUpload(sceneId, shot.id, croppedFile)
    setCropperModalVisible(false)
    setSelectedImageFile(null)
  }

  const handleCropperCancel = () => {
    setCropperModalVisible(false)
    setSelectedImageFile(null)
  }

  const triggerFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleDuplicateShotConfirm = position => {
    setFormData(prev => {
      const newShotList = [...(prev.shotList || [])]
      const sceneIndex = newShotList.findIndex(s => s.id === sceneId)
      if (sceneIndex > -1) {
        const scene = { ...newShotList[sceneIndex] }
        const shots = [...(scene.shots || [])]

        const currentShotIndex = shots.findIndex(s => s.id === shot.id)
        if (currentShotIndex > -1) {
          const duplicatedShot = { ...shot }

          if (position === 'before') {
            shots.splice(currentShotIndex, 0, duplicatedShot)
          } else {
            shots.splice(currentShotIndex + 1, 0, duplicatedShot)
          }

          // Re-index all shots so their id and key are strictly sequential
          scene.shots = shots.map((s, index) => {
            const nextNumber = index + 1
            return {
              ...s,
              id: `${sceneId}-shot-${nextNumber}`,
              key: `shot ${nextNumber}`
            }
          })

          newShotList[sceneIndex] = scene
        }
      }
      return { ...prev, shotList: newShotList }
    })
    setDuplicateModalVisible(false)
  }

  const handleCopyImageConfirm = targetValue => {
    if (!targetValue) return

    const [targetSceneId, targetShotId] = targetValue.split('::')
    handleCopyImageToShot(sceneId, shot.id, targetSceneId, targetShotId)
    setCopyImageModalVisible(false)
  }

  return (
    <div className="shot-card">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept="image/*"
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <h3
          className="shot-title"
          style={{ margin: 0, display: 'flex', alignItems: 'center' }}
        >
          {DragHandle && <DragHandle />}
          Shot {shotIndex}
        </h3>
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="duplicate-shot"
                onClick={() => setDuplicateModalVisible(true)}
              >
                <Icon type="copy" /> Duplicate Shot
              </Menu.Item>
              <Menu.Item
                key="delete-shot"
                onClick={() => handleDeleteShot(sceneId, shot.id)}
              >
                <Icon type="delete" /> Delete Shot
              </Menu.Item>
            </Menu>
          }
          trigger={['click']}
          placement="bottomRight"
        >
          <Button type="link" size="small">
            Action <Icon type="down" />
          </Button>
        </Dropdown>
      </div>

      <div
        className="image-wrapper"
        style={
          shot.error
            ? { border: '1px solid #ff4d4f', position: 'relative' }
            : { position: 'relative' }
        }
      >
        {(shot.isGenerating || shot.isUploading) && shot.progress !== 100 && (
          <div className="progress-overlay">
            <Spin style={{ color: '#fff' }} size="large" />
            <span className="progress-text">
              {shot.isUploading
                ? 'Uploading...'
                : shot.progress
                ? 'Generated Successfully'
                : 'Generating...'}
            </span>
          </div>
        )}
        {!shot.isGenerating && shot.error && (
          <div
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              background: '#fff',
              border: '1px solid #ff4d4f',
              color: '#ff4d4f',
              borderRadius: 4,
              padding: '2px 6px',
              fontSize: 12,
              fontWeight: 600
            }}
          >
            Task failed. Please regenerate.
          </div>
        )}
        {shot.imageSrc && (
          <ImagePreview
            key={shot.imageSrc}
            src={shot.imageSrc || '/api/placeholder/400/300'}
            alt={`Shot ${shot.id}`}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        )}

        {/* History Button - Absolutely Positioned */}
        {shot.images_history && shot.images_history.length > 0 && (
          <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 10 }}>
            <Tooltip title={`View History (${shot.images_history.length})`}>
              <Button
                shape="round"
                onClick={() => setHistoryModalVisible(true)}
                disabled={shot.isGenerating}
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.45)',
                  color: '#fff',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  padding: '4px 12px',
                  height: 'auto',
                  transition: 'all 0.3s ease'
                }}
                className="history-pill-button"
              >
                <Icon type="history" style={{ fontSize: 16 }} />
                <span
                  style={{
                    marginLeft: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    lineHeight: 1
                  }}
                >
                  {shot.images_history.length}
                </span>
              </Button>
            </Tooltip>
          </div>
        )}
      </div>

      <EntitySelectorList
        activePrompt={shot.initial_image_prompt}
        onUpdatePrompt={newText =>
          updateShotDataRef.current(shot.id, 'initial_image_prompt', newText)
        }
        formData={{ ...formData, setFormData }}
        disabled={shot.isGenerating}
        videoId={videoId}
        isFetchingCharacters={isFetchingCharacters}
      />

      <CommentInput
        disabled={shot.isGenerating}
        text={shot.initial_image_prompt}
        placeholder="Write your image prompt..."
        characterList={formData.characterList}
        locationReference={formData.locationReference}
        selectedCharacter={formData.selectedCharacter}
        locationMap={formData.locationMap}
        outfitMap={formData.outfitMap}
        outfitsReference={formData.outfits_reference}
        outfits={formData.outfits}
        propMap={formData.propMap}
        propsReference={formData.props_reference || formData.propReference}
        onChange={newText =>
          updateShotDataRef.current(shot.id, 'initial_image_prompt', newText)
        }
      />

      <DialogueEditor
        dialogue={shot.dialogue}
        disabled={shot.isGenerating}
        formData={formData}
        onChange={newDialogue =>
          updateShotDataRef.current(shot.id, 'dialogue', newDialogue)
        }
      />

      <div className="card-actions" style={{ justifyContent: 'space-between' }}>
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="regenerate-image"
                onClick={() => handleRegenerateImage(sceneId, shot.id)}
                disabled={shot.isGenerating}
              >
                <Icon type="reload" /> Regenerate Image
              </Menu.Item>
              {/* <Menu.Item
                key="regenerate-with-reference"
                onClick={() => handleRegenerateWithReference(sceneId, shot.id)}
                disabled={shot.isGenerating}
              >
                <Icon type="file-image" /> Edit Image (Single Reference)
              </Menu.Item> */}

              <Menu.Item
                key="regenerate-with-reference-nano-banana"
                onClick={() =>
                  handleRegenerateWithReferenceNanoBanana(sceneId, shot.id)
                }
                disabled={
                  shot.isGenerating || isUploadingImage || !shot.imageSrc
                }
              >
                <Icon type="file-image" /> Edit Image (Multiple References)
              </Menu.Item>
              <Menu.Item
                key="upload-image"
                onClick={triggerFileUpload}
                disabled={shot.isGenerating || isUploadingImage}
              >
                <Icon type="upload" /> Upload Image
              </Menu.Item>
              <Menu.Item
                key="copy-image"
                onClick={() => setCopyImageModalVisible(true)}
                disabled={
                  shot.isGenerating ||
                  isUploadingImage ||
                  !shot.imageSrc ||
                  availableCopyTargets.length === 0
                }
              >
                <Icon type="copy" /> Copy Image To Shot
              </Menu.Item>
            </Menu>
          }
          trigger={['click']}
          placement="bottomLeft"
        >
          <Button
            icon="reload"
            disabled={shot.isGenerating}
            style={{ minWidth: 0, overflow: 'hidden' }}
          >
            <span
              style={{
                marginLeft: 4,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              Regenerate
            </span>
            <Icon type="down" style={{ fontSize: 10, marginLeft: 4 }} />
          </Button>
        </Dropdown>

        <Button
          icon="setting"
          onClick={() => handleEditSettings(sceneId, shot.id)}
          disabled={shot.isGenerating}
          style={{ minWidth: 0, overflow: 'hidden' }}
        >
          <span
            style={{
              marginLeft: 4,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            Shot Editor
          </span>
        </Button>
      </div>

      <ImageHistoryModal
        visible={historyModalVisible}
        history={shot.images_history || []}
        onCancel={() => setHistoryModalVisible(false)}
        onSelect={url => {
          updateShotDataRef.current(shot.id, 'imageSrc', url)
          setHistoryModalVisible(false)
        }}
        onDelete={index => {
          const updatedHistory = [...(shot.images_history || [])]
          updatedHistory.splice(index, 1)
          updateShotDataRef.current(shot.id, 'images_history', updatedHistory)
        }}
      />

      <ImageCropperModal
        visible={cropperModalVisible}
        imageFile={selectedImageFile}
        aspectRatio={formData.aspectRatio || '16:9'}
        onCancel={handleCropperCancel}
        onCrop={handleCropComplete}
      />

      <DuplicateShotModal
        visible={duplicateModalVisible}
        shotIndex={shotIndex}
        onCancel={() => setDuplicateModalVisible(false)}
        onConfirm={handleDuplicateShotConfirm}
      />

      <CopyImageModal
        visible={copyImageModalVisible}
        sourceLabel={sourceLabel}
        sourceImageSrc={shot.imageSrc}
        targets={availableCopyTargets}
        onCancel={() => setCopyImageModalVisible(false)}
        onConfirm={handleCopyImageConfirm}
      />
    </div>
  )
}

export default ShotCard
